import React, { useState, useEffect, useRef } from 'react';
import {
  Alert,
  Card,
  Button,
  Table,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Row,
  Col,
  Space,
  Tag
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useCondoStore } from '../store/condoStore';
import { useAuthStore } from '../store/authStore';
import { Expense } from '../types';
import dayjs from 'dayjs';
import { staticApi } from '../utils/staticApi';

import { formatDate } from '../utils/date';
const { Option } = Select;

export const Expenses: React.FC = () => {
  const { expenses, createExpense, updateExpenseApi, deleteExpenseApi, fetchExpenses, errorByKey } = useCondoStore();
  // R11-3 — a failed expenses fetch used to be swallowed silently (table just
  // showed "No data"), hiding the 403 this page shipped with. Surface it.
  const expensesError = errorByKey?.expenses ?? null;
  const { currentCondo } = useAuthStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [form] = Form.useForm();
  const confirmModalRef = useRef<{ destroy: () => void } | null>(null);

  useEffect(() => {
    fetchExpenses(currentCondo?.id);
    return () => {
      confirmModalRef.current?.destroy();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; fetchExpenses is a stable zustand action
  }, [currentCondo?.id]);

  const handleSubmit = async (values: any) => {
    try {
      if (editingExpense) {
        await updateExpenseApi(editingExpense.id, {
          ...values,
          date: new Date(values.date),
          condoId: editingExpense.condoId,
        });
        staticApi.message.success('Expense updated successfully');
      } else {
        await createExpense({
          condoId: currentCondo?.id || '',
          category: values.category,
          description: values.description,
          amount: values.amount,
          date: new Date(values.date),
          vendor: values.vendor,
        });
        staticApi.message.success('Expense added successfully');
      }

      setIsModalOpen(false);
      setEditingExpense(null);
      form.resetFields();
    } catch {
      // error already set in store
    }
  };

  const handleEdit = (expense: Expense) => {
    setEditingExpense(expense);
    // Sprint 145 (R14-9) — `toISOString()` is UTC, so opening the edit form on
    // an expense dated the 19th showed the 20th for any local time at or after
    // 20:00 EDT, and re-saving persisted the shifted date. `dayjs()` formats in
    // local time. The string branch is already a bare date and needs no parse.
    const dateStr = expense.date instanceof Date
      ? dayjs(expense.date).format('YYYY-MM-DD')
      : String(expense.date).split('T')[0];
    form.setFieldsValue({
      ...expense,
      date: dateStr,
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id: string) => {
    confirmModalRef.current = staticApi.modal.confirm({
      title: 'Are you sure you want to delete this expense?',
      content: 'This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteExpenseApi(id);
          staticApi.message.success('Expense deleted successfully');
        } catch {
          staticApi.message.error('Failed to delete expense');
        }
      },
    });
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      maintenance: 'blue',
      utilities: 'green',
      staff: 'orange',
      insurance: 'red',
      repairs: 'purple',
      other: 'gray',
    };
    return colors[category] || 'gray';
  };

  const columns = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => (
        <Tag color={getCategoryColor(category)}>
          {category.charAt(0).toUpperCase() + category.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `$${Number(amount).toLocaleString()}`,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      // Sprint 145 (R14-10) — this column carried its own local fix for the
      // UTC-midnight bug ('T00:00:00' appended to force a local parse). Correct,
      // and one of several divergent copies of the rule. `formatDate` owns it.
      render: (date: Date | string) => formatDate(date),
    },
    {
      title: 'Vendor',
      dataIndex: 'vendor',
      key: 'vendor',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Expense) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Expenses</h1>
          <p className="text-gray-600">Manage condo expenses</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingExpense(null);
            form.resetFields();
            setIsModalOpen(true);
          }}
        >
          Add Expense
        </Button>
      </div>

      {expensesError && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message="Couldn't load expenses"
          description={expensesError}
        />
      )}

      <Card>
        <Table
          columns={columns}
          dataSource={expenses}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 800 }}
        />
      </Card>

      <Modal
        title={editingExpense ? 'Edit Expense' : 'Add New Expense'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingExpense(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <Input placeholder="Enter expense description" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: 'Please select category' }]}
              >
                <Select placeholder="Select category">
                  <Option value="maintenance">Maintenance</Option>
                  <Option value="utilities">Utilities</Option>
                  <Option value="staff">Staff</Option>
                  <Option value="insurance">Insurance</Option>
                  <Option value="repairs">Repairs</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="amount"
                label="Amount"
                rules={[{ required: true, message: 'Please enter amount' }]}
              >
                <InputNumber
                  placeholder="Enter amount"
                  min={0}
                  step={0.01}
                  style={{ width: '100%' }}
                  formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="date"
                label="Date"
                rules={[{ required: true, message: 'Please select date' }]}
              >
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="vendor"
                label="Vendor"
              >
                <Input placeholder="Enter vendor name" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item className="mb-0">
            <div style={{display:"flex",justifyContent:"flex-end",gap:"12px"}}>
              <Button
                onClick={() => {
                  setIsModalOpen(false);
                  setEditingExpense(null);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingExpense ? 'Update' : 'Add'} Expense
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
